import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useInvertedCursor } from '../../hooks/useInvertedCursor';
import { SEO } from '../../components/SEO';
import './TermsOfService.css';

const FadeInSection = ({ children, className = '', delay = '' }) => {
    const [ref, isVisible] = useIntersectionObserver({ triggerOnce: true, threshold: 0.1 });
    return (
        <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className} ${delay}`}>
            {children}
        </div>
    );
};

const TermsOfService = () => {
    useInvertedCursor();
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <SEO 
                title="Terms of Service | MAD OVER TECHNOLOGY"
                description="Terms of Service for MAD OVER TECHNOLOGY. Read our terms and conditions for using our website, products, and services."
                keywords="Terms of Service, Terms and Conditions, Legal, MAD OVER TECHNOLOGY"
                url="/terms-of-service"
            />
            <main className="legal-page">
                <section className="legal-hero">
                    <div className="container mx-auto px-4">
                        <FadeInSection>
                            <h1 className="legal-title">Terms of Service</h1>
                            <p className="legal-subtitle">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </FadeInSection>
                    </div>
                </section>

                <section className="legal-content">
                    <div className="container mx-auto px-4 max-w-4xl">
                        <FadeInSection>
                            <div className="legal-text">
                                <p>Welcome to MAD OVER TECHNOLOGY ("Company", "we", "our", "us").</p>
                                
                                <p>These Terms of Service ("Terms") govern your use of our website, products, and services located at madovertech.io and any related subdomains or pages (collectively, the "Service").</p>
                                
                                <p>By accessing or using the Service, you agree to be bound by these Terms.</p>

                                <h2>1. Use of the Service</h2>
                                <p>You must be at least 18 years old to use our Service. You agree to use the Service only for lawful purposes and in accordance with these Terms.</p>

                                <h2>2. Accounts</h2>
                                <p>To use certain features, you may need to create an account. You agree to provide accurate information and keep your login details secure. You are responsible for all activity under your account.</p>

                                <h2>3. Purchases & Payments</h2>
                                <p>If you purchase any digital products, subscriptions, or services from us:</p>
                                <ul>
                                    <li>Payments are processed securely by third-party providers such as Paddle.</li>
                                    <li>You authorize us and Paddle to charge your selected payment method.</li>
                                    <li>Subscription services automatically renew unless canceled before the renewal date.</li>
                                </ul>

                                <h2>4. Intellectual Property</h2>
                                <p>All content, branding, code, graphics, and materials on the Service are owned by MAD OVER TECHNOLOGY or licensed to us. You may not copy, modify, reproduce, or distribute any content without permission.</p>

                                <h2>5. Prohibited Activities</h2>
                                <p>You may not:</p>
                                <ul>
                                    <li>Use the Service for unlawful, fraudulent, or harmful activities</li>
                                    <li>Attempt to hack, damage, or disrupt our Service</li>
                                    <li>Copy or resell our digital content without authorization</li>
                                </ul>

                                <h2>6. Termination</h2>
                                <p>We may suspend or terminate your access to the Service for violating these Terms or for any activity that harms our business or users.</p>

                                <h2>7. Disclaimer</h2>
                                <p>Our services are provided on an "as-is" and "as-available" basis. We do not guarantee uninterrupted or error-free operation.</p>

                                <h2>8. Limitation of Liability</h2>
                                <p>To the maximum extent permitted by law, MAD OVER TECHNOLOGY is not responsible for:</p>
                                <ul>
                                    <li>Loss of profits</li>
                                    <li>Loss of data</li>
                                    <li>Indirect or consequential damages</li>
                                </ul>
                                <p>You use our Service at your own risk.</p>

                                <h2>9. Changes to Terms</h2>
                                <p>We may update these Terms occasionally. Continued use of the Service constitutes acceptance of any changes.</p>

                                <h2>10. Contact Us</h2>
                                <p>If you have questions about these Terms, email us at:</p>
                                <p><a href="mailto:hello@madovertech.io">hello@madovertech.io</a></p>
                            </div>
                        </FadeInSection>
                    </div>
                </section>

                {/* Footer */}
                <footer className="footer">
                    <div className="footer-links">
                        <Link to="/">Home</Link>
                        <Link to="/ai-photobooth">AI Photo Booth</Link>
                        <a href="https://nexasync.io" target="_blank" rel="noopener noreferrer">NexaSync</a>
                        <Link to="/terms-of-service">Terms of Service</Link>
                        <Link to="/privacy-policy">Privacy Policy</Link>
                        <Link to="/refund-policy">Refund Policy</Link>
                        <a href="https://www.instagram.com/madovertechnology" target="_blank" rel="noopener noreferrer">Instagram</a>
                        <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20learning%20more%20about%20your%20services" target="_blank" rel="noopener noreferrer">Contact</a>
                    </div>
                    <div className="footer-locations">
                        <span>DELHI | BENGALORE | MUMBAI</span>
                    </div>
                    <div className="footer-contact">
                        <a href="mailto:hello@madovertech.io" className="footer-email">hello@madovertech.io</a>
                        <span className="footer-separator">|</span>
                        <a href="tel:+919711689861" className="footer-phone">+91 9711689861</a>
                    </div>
                    <p className="footer-copyright">© 2025 Mad Over Technology. All rights reserved.</p>
                </footer>
            </main>
        </>
    );
};

export default TermsOfService;
